<script>
import OmniTextarea from "@/components/inputs/Textarea.vue";
import OrdersMixin from "@/views/orders/OrdersMixin.vue";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import { FacilityType } from "@/data/facility";
import OrderStopStatus from "@/components/status/OrderStopStatus.vue";

export default {
  name: "OrderViewStops",
  components: { OrderStopStatus, OmniTextarea },
  mixins: [GoogleMapsMixin, OrdersMixin],
  props: {
    order: Object,
  },
  methods: {
    getFacilityMapUrl(facility) {
      if (facility.type === FacilityType.point && facility.preciseCoordinates) {
        return this.getPlaceUrl(facility.preciseCoordinates);
      }
      return this.getPlaceUrlByAddress(
        facility.addressLine ? facility.addressLine + ", " + facility.address : facility.address,
      );
    },
    getStopFreights(stop) {
      return this.order.freights.filter((f) => stop.freights.includes(f.id));
    },
  },
};
</script>

<template>
  <div class="user-block pt-5 pr-5 pb-7 pl-5 bg-white rounded mt-4">
    <v-row>
      <v-col>
        <div class="text-grey-darken-3 mb-6 font-weight-medium">Stops</div>
        <template v-for="(stop, index) in order.orderStops" :key="stop.id">
          <v-row data-qa="view-stop-row">
            <v-col class="timeline-col">
              <div class="timeline">
                <div class="timeline--bullet">{{ index + 1 }}</div>
                <div v-if="index < order.orderStops.length - 1" class="timeline--line"></div>
              </div>
            </v-col>
            <v-col>
              <div class="bg-grey-lighten-4 rounded pa-4">
                <v-row>
                  <v-col>
                    <div class="d-flex align-center font-size-12 mb-3">
                      <template v-if="stop.type === STOP_PICK_UP">
                        <v-icon class="text-success" size="20">mdi-package-variant-closed</v-icon>
                        <span class="font-weight-600 text-blue-grey-darken-1 ml-2">Pick up</span>
                      </template>
                      <template v-if="stop.type === STOP_DELIVERY">
                        <v-icon class="text-error">mdi-map-marker-radius-outline</v-icon>
                        <span class="font-weight-600 text-blue-grey-darken-1 ml-2">Delivery</span>
                      </template>
                      <order-stop-status :stop="stop" class="ml-2"></order-stop-status>
                    </div>
                    <div class="mb-2 text-grey-darken-2 text-caption" data-qa="facility-type">
                      Type: {{ facilityTypesMap[stop.facility.type] }}
                    </div>
                    <div class="mb-2" data-qa="facility-name">
                      <router-link
                        :to="{ name: 'facilities-edit', params: { id: stop.facilityId } }"
                        class="font-size-16 text-decoration-none text-primary font-weight-500"
                      >
                        {{ stop.facility?.name }}
                      </router-link>
                    </div>
                    <div class="d-flex align-center text-grey-darken-2 mb-5" data-qa="facility-address">
                      <span v-if="stop.facility?.addressLine">{{ stop.facility?.addressLine }},&nbsp;</span>
                      {{ stop.facility?.address }}
                      <a :href="getFacilityMapUrl(stop.facility)" class="ml-2" target="_blank">
                        <v-icon color="success" icon="mdi-map-marker-radius-outline"></v-icon>
                      </a>
                    </div>
                    <div class="d-flex align-center text-grey-darken-1 font-weight-500 mb-5" data-qa="freight-time">
                      <v-icon class="mr-2" size="20">mdi-clock-outline</v-icon>
                      <span>{{ getTimeType(stop.timeType) }} {{ getStopTime(stop) }}</span>
                    </div>
                    <div>
                      <div
                        v-for="(freight, freightIndex) in getStopFreights(stop)"
                        :key="freight.id"
                        data-qa="freight-data"
                      >
                        <span class="text-grey-darken-1 mr-2">Freight #{{ freightIndex + 1 }}:</span>
                        <span class="text-grey-darken-2 font-weight-500">
                          {{ freight.quantity }} {{ getFreightTypeName(freight)?.toLowerCase() }}
                          {{ freight.weight }} lbs
                        </span>
                      </div>
                    </div>
                  </v-col>
                  <v-col>
                    <omni-textarea
                      :model-value="stop.note"
                      data-qa="load-stop-note"
                      label="Load stop note"
                      readonly
                      rows="6"
                    >
                    </omni-textarea>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>
          <div v-if="index < order.orderStops.length - 1" class="distance-gap">
            <div class="distance-gap--distance text-grey-darken-1">
              {{ getStopDistance(order.orderStops, order.distances, index) }} miles
            </div>
            <div class="distance-gap--line"></div>
          </div>
        </template>
        <div class="text-grey-darken-1 font-size-12 total-distance">
          Loaded miles: {{ $filters.miles(order.totalDistance) }} miles
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
.timeline-col {
  flex: 0 0 16px;
  margin-right: 20px;
}

.timeline {
  position: relative;
  height: 100%;

  .timeline--bullet {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 10px;
    width: 16px;
    height: 16px;
    padding: 2px;
    border-radius: 20px;
    background-color: #9e9e9e;
    color: white;
    font-size: 10px;
    font-weight: 500;
  }

  .timeline--line {
    position: absolute;
    left: 7px;
    top: 26px;
    width: 2px;
    bottom: -42px;
    background-color: #e0e0e0;
  }
}

.distance-gap {
  position: relative;
  margin: 10px 0 10px 44px;
  height: 12px;

  .distance-gap--distance {
    position: absolute;
    background-color: #ffffff;
    padding: 0 12px;
    top: -5px;
    left: 45%;
    font-size: 10px;
    font-weight: 500;
    z-index: 10;
  }

  .distance-gap--line {
    position: absolute;
    top: 5px;
    height: 1px;
    width: 100%;
    background-color: #e0e0e0;
  }
}

.total-distance {
  margin: 7px 44px;
}
</style>
